import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   computed,
   inject,
   Injector,
   OnDestroy,
   Signal,
   signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectOpenDialogEvent,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectStateInitEventPayload,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { extensionHuntGroupsOptionSelectors } from 'src/app/common-components/entity-select/extension/state/extension-option-reducers/extension-huntgroup-option.reducer';
import { extensionLoadHuntGroupsActions } from 'src/app/common-components/entity-select/extension/state/extension-option.actions';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ReportParameterComponent } from '../../report-parameter-list/report-parameter-component';
import { ReportParameterConfig } from '../../report-parameter-list/report-parameter.config';
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: HuntGroupExtensionsReportParameterComponent }],
    selector: 'ndt-hunt-group-extensions-report-parameter',
    templateUrl: './hunt-group-extensions-report-parameter.component.html',
    styleUrls: ['./hunt-group-extensions-report-parameter.component.scss'],
    standalone: false
})
export class HuntGroupExtensionsReportParameterComponent implements OnDestroy, ReportParameterComponent {
   form: UntypedFormGroup;

   selectedAccountId: number | null;

   searchControl = new UntypedFormControl('');

   private destroyed$ = new Subject<void>();

   config: ReportParameterConfig;

   panelDisabled: boolean;

   entityName = 'Hunt Groups';

   huntGroupType = 5;

   readonly store = inject(Store);
   readonly cd = inject(ChangeDetectorRef);
   readonly injector = inject(Injector);

   readonly allExtensions = this.store.selectSignal(extensionHuntGroupsOptionSelectors.selectAll);
   readonly extensions = this.store.selectSignal(extensionHuntGroupsOptionSelectors.selectFilteredItems);
   readonly extensionsCallState = this.store.selectSignal(extensionHuntGroupsOptionSelectors.selectCallState);

   // Store toSignal results outside of computed
   private _huntGroupExtensionItemsSignal = signal<Signal<ExtensionOption[]> | null>(null);
   private _allSignal = signal<Signal<boolean> | null>(null);

   readonly extensionItemsFromControl = computed(
      () => {
         const sig = this._huntGroupExtensionItemsSignal();
         return sig ? sig() : [];
      },
      { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
   );

   readonly allFromControl = computed(() => {
      const sig = this._allSignal();
      return sig ? sig() : false;
   });

   private updateHuntGroupExtensionItemsSignal() {
      const extensionItemsControl = this.form?.controls?.extensions;
      if (extensionItemsControl) {
         const sig = toSignal<ExtensionOption[]>(
            extensionItemsControl.valueChanges.pipe(map(() => extensionItemsControl.getRawValue() ?? [])),
            {
               initialValue: extensionItemsControl.getRawValue() ?? [],
               injector: this.injector,
            }
         ) as Signal<ExtensionOption[]>;
         this._huntGroupExtensionItemsSignal.set(sig);
      } else {
         this._huntGroupExtensionItemsSignal.set(null);
      }
   }

   private updateAllSignal() {
      const allControl = this.form?.controls?.all;
      if (allControl) {
         const sig = toSignal(allControl.valueChanges.pipe(map(() => allControl.getRawValue() ?? false)), {
            initialValue: allControl.getRawValue() ?? false,
            injector: this.injector,
         });
         this._allSignal.set(sig);
      } else {
         this._allSignal.set(null);
      }
   }

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<ExtensionOption>>(() => {
      return {
         displayOptions: {
            label: this.entityName,
            displayType: LcsSelectWithAllDisplayTypes.Label,
            displayWithFn: (
               items: ExtensionOption[],
               state: LcsAdvancedMultiSelectReadonlyStateType<ExtensionOption>
            ) => {
               const options = this.extensionItemsFromControl() ?? [];
               if (options.length === 1 && options[0].name) {
                  return `${options[0].number} - ${options[0].name}`;
               }
               return `${options.length} ${this.entityName}`;
            },
            hasAllCheckbox: true,
         },
         group: new LcsAdvancedMultiSelectTriggerFormGroup<ExtensionOption>(
            {
               items: new FormControl(this.extensionItemsFromControl() ?? [], { nonNullable: true }),
               all: new FormControl(this.allFromControl(), { nonNullable: true }),
            },
            {
               validators: this.form.hasValidator(Validators.required) ? [Validators.required] : [],
            }
         ),
         syncOn: 'save',
      };
   });

   readonly selectedExtensions = computed(() => {
      return signal(this.extensionItemsFromControl());
   });

   readonly dataSource = computed<LcsAdvancedMultiSelectDatasource<ExtensionOption>>(() => {
      return {
         id: 1,
         label: this.entityName,
         items: this.availableExtensions() ?? [],
         selectedItems: this.selectedExtensions()(),
         itemTrackBy: this.extensionsTrackBy,
         displayOptions: {
            displayWithFn: (item: ExtensionOption) => {
               return item.name ?? '';
            },
         },
         callState: this.extensionsCallState(),
      };
   });

   readonly availableExtensions = computed(() => {
      const Extensions = this.extensions();
      return Extensions;
   });

   constructor() {
      this.store
         .select(getSelectedAccountId)
         .pipe(takeUntil(this.destroyed$))
         .subscribe((selectedAccountId) => {
            if (selectedAccountId) {
               this.selectedAccountId = selectedAccountId;
               this.form?.controls?.accountId?.setValue(this.selectedAccountId);
               this.store.dispatch(
                  extensionLoadHuntGroupsActions.load({
                     additionalFields: [],
                     filterOverrides: [
                        `accountId = ${this.selectedAccountId}`,
                        `type = ${this.huntGroupType}`,
                        `isArchived = false`,
                     ],
                  })
               );
            }
         });
   }

   initializeSignals() {
      this.updateHuntGroupExtensionItemsSignal();
      this.updateAllSignal();
      setTimeout(() => this.cd.markForCheck(), 0); //Force form to display required * on huntgroups control
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onSelectAll(isChecked: boolean, panel: any) {
      if (isChecked) {
         this.panelDisabled = true;
         this.form.get('extensions')?.disable();
         panel.selectAll();
      } else {
         this.panelDisabled = false;
         this.form.get('extensions')?.enable();
         panel.deselectAll();
      }

      this.form.controls.Extensions?.updateValueAndValidity();
   }

   onAllChange(all: boolean) {
      this.form.controls.all.setValue(all);
   }

   onInit(e: LcsAdvancedMultiSelectStateInitEventPayload<ExtensionOption>) {
      this.initializeSignals();

      this.store.dispatch(
         extensionLoadHuntGroupsActions.load({
            additionalFields: [],
            filterOverrides: [
               `accountId = ${this.selectedAccountId}`,
               `type = ${this.huntGroupType}`,
               `isArchived = false`,
            ],
         })
      );
      this.form?.controls?.accountId?.setValue(this.selectedAccountId);
   }

   onOpen(e: LcsAdvancedMultiSelectOpenDialogEvent<ExtensionOption>) {
      this.store.dispatch(extensionLoadHuntGroupsActions.searchReset());
   }

   onSave(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['save']) {
      const selectedItems = e.state.selectedItems();
      this.form.controls.extensions.setValue(selectedItems);
   }

   onSearch(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['search']) {
      const searchText = e.searchText.replace(/[()-]/g, '');
      this.store.dispatch(extensionLoadHuntGroupsActions.search({ searchTerm: searchText }));
   }

   onSelectedItemsChange(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['selectedItemsChange']) {
      this.selectedExtensions().set(e.state.selectedItems() as ExtensionOption[]);
   }

   onClose(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['close']) {
      const datasource = this.dataSource();
      if (datasource) {
         e.actions.datasourceChange({ datasource });
      }
      this.selectedExtensions().set(this.extensionItemsFromControl());
   }

   isExtensionSelected(selectedExtensions: ExtensionOption[], extension: ExtensionOption) {
      return selectedExtensions.some((e) => this.extensionsTrackBy(e) === this.extensionsTrackBy(extension));
   }

   private extensionsTrackBy = (extension: ExtensionOption) => extension.extensionId;
}
